<template>
  <PageHeader :showBreadcrumb="true" />
  <div class="container p-3 text-center">
    <!-- Display All Combined Exams -->
    <div class="row mb-2 g-3">
      <div class="col-lg-12 col-md-12">
        <FormSelectGroup
            label="Select Exam"
            inputId="combined-exam-input"
            :required="true"
            state
        >
          <template #default>
            <PvSelect
                id="combined-exam-input"
                v-model="selectedCombined"
                :options="combinedExamOptions"
                :optionLabel="formatExamLabel"
                optionValue="examProductId"
                placeholder="-- Select Entity • Exam • Product --"
                filter
                autoOptionFocus
                @change="onChangeCombined"
            />
          </template>
        </FormSelectGroup>
      </div>
    </div>
    <!-- Documents Upload/Delete Section -->
    <div v-if="selectedCombined" class="row">
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="mb-3">QA Exam Clarifications</h2>
          <FormSelectGroup
              label="QA Clarification Module Type"
              inputId="qa-module-type"
              :required="true"
          >
            <template #default>
              <PvSelect
                  id="qa-module-type"
                  v-model="selectedQAModuleType"
                  :options="qaModuleOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="-- Select QA Module Type --"
                  @change="onChangeQaClarification"
              />
            </template>
          </FormSelectGroup>
          <MNFileUpload
              v-if="selectedQAModuleType"
              module-type="Quality Assurance File Uploads"
              label="QA Exam Clarifications"
              ref="qaClarificationFiles"
              name="qaClarificationFiles"
              accept="application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroenabled.12,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,text/plain,image/png,image/jpeg,application/zip,application/x-zip-compressed"
              :examProduct="selectedCombined"
              :qaModuleType="selectedQAModuleType"
              :fileLimit="1000"
              :hideLabel=true
          />

        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="border portal-box bg-light shadow-sm rounded p-4 mb-2">
          <h2 class="mb-3">TCA Clarification Files</h2>
          <FormSelectGroup
              label="TCA Clarification Module Type"
              inputId="tca-module-type"
              :required="true"
          >
            <template #default>
              <PvSelect
                  id="tca-module-type"
                  v-model="selectedTCAModuleType"
                  :options="tcaModuleOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="-- Select TCA Module Type --"
                  @change="onChangeTcaClarification"
              />
            </template>
          </FormSelectGroup>
          <MNFileUpload
              v-if="selectedTCAModuleType"
              module-type="Quality Assurance File Uploads"
              label="TCA Clarification Files"
              ref="tcaClarificationFiles"
              name="tcaClarificationFiles"
              accept="application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroenabled.12,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,text/plain,image/png,image/jpeg,application/zip,application/x-zip-compressed"
              :examProduct="selectedCombined"
              :qaModuleType="selectedTCAModuleType"
              :fileLimit="1000"
              :hideLabel=true
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import PageHeader from "@/components/common/PageHeader.vue";
import MNFileUpload from "@/components/common/MNFileUpload.vue";
import FormSelectGroup from "@/components/common/FormSelectGroup.vue";
import {useAppStore} from "@/stores/appStore.js";
import {computed, ref} from "vue";
import {useAuthStore} from "@/stores/authStore.js";
import {useForm} from "@/composables/form.js";
import { QualityAssuranceFileUploadsModel } from '@/models/QualityAssuranceFileUploadsModel';
import {
  QA_EXAM_CLARIFICATION_MODULE_TYPES,
  TCA_CLARIFICATION_MODULE_TYPES,
  getAsFormOptions
} from '@/utils/constants.js';

const store = useAuthStore();
const appStore = useAppStore();
const selectedCombined = ref();

const selectedQAModuleType = ref(null);
const selectedTCAModuleType = ref(null);
const qaModuleOptions = getAsFormOptions(QA_EXAM_CLARIFICATION_MODULE_TYPES);
const tcaModuleOptions = getAsFormOptions(TCA_CLARIFICATION_MODULE_TYPES);

const qaClarificationFiles = ref();
const tcaClarificationFiles = ref();

const formState = useForm(new QualityAssuranceFileUploadsModel());

const combinedExamOptions = computed(() => {
  const rawOptions = appStore.getAllEntityExamProductOptions();

  const groupedMap = new Map();

  rawOptions.forEach(option => {
    const key = `${option.entityName}__${option.examType}`;

    if (!groupedMap.has(key)) {
      groupedMap.set(key, {
        ...option,
        productNameSet: new Set([option.productName])
      });
    } else {
      groupedMap.get(key).productNameSet.add(option.productName);
    }
  });

  return Array.from(groupedMap.values()).map(group => ({
    ...group,
    combinedProductName: Array.from(group.productNameSet).join(', ')
  }));
});

const onChangeCombined = async ({ value }) => {
  if (!value) return;
  selectedCombined.value = value;
  formState.state.data.examProductId  = value;
  await clearAllUploaders();
};

const onChangeQaClarification = async ({ value }) => {
  if (!value) return;
  await clearQaClarificationUploader();
};

const onChangeTcaClarification = async ({ value }) => {
  if (!value) return;
  await clearTcaClarificationUploader();
};

const formatExamLabel = (option) => {
  if (!option) return '';
  return `(${option.entityName} • ${option.examType} • ${option.combinedProductName})`;
};

const clearAllUploaders = async () => {
  qaClarificationFiles.value?.clear();
  tcaClarificationFiles.value?.clear();
};

const clearQaClarificationUploader = async () => {
  qaClarificationFiles.value?.clear();
};

const clearTcaClarificationUploader = async () => {
  tcaClarificationFiles.value?.clear();
};
</script>

<style scoped>

</style>